// Group score sheets — a printable, fill-in-by-hand record of a pool's
// round-robin. The organizer prints one sheet per group and hands it to the
// players, who write down how each match went (game-by-game) and, once the pool
// is done, the final standings.
//
// This module is pure data: it turns a `TournamentState` into a framework-free
// model describing what belongs on each sheet. Rendering (HTML/print) lives in
// the UI layer so the domain stays testable and free of presentation concerns.

import type { BracketNode, Cell, PlayerId, TournamentState } from './types';
import { gamesToWin, normalizeBestOf } from './scoring';
import { timeSlotLabel } from './ids';
import { DEFAULT_BRACKET_BEST_OF, DEFAULT_GROUP_BEST_OF } from './types';

/** A player as listed on a score sheet. */
export interface ScoreSheetPlayer {
  id: PlayerId; // "P1" — per-tournament named-block id
  seed: number; // 1-based global entry index
  name: string; // display name, falling back to the id
}

/** One round-robin match, in the order it is played within the group. */
export interface ScoreSheetMatch {
  cellId: string; // Cell_PoolA_P1_P3
  order: number; // 1-based play order within the group (by time slot, then table)
  timeSlot: number; // 1-based Time_Slot index
  timeSlotLabel: string; // "Time_Slot_1"
  tableIndex: number; // 1-based table within the time slot
  playerA: ScoreSheetPlayer; // row player
  playerB: ScoreSheetPlayer; // column player
  bestOf: number; // series length for this match (odd)
  gamesToWin: number; // games needed to take the series (derived from bestOf)
}

/** Everything needed to print one group's fill-in sheet. */
export interface GroupScoreSheet {
  tournamentId: string;
  tournamentName: string;
  poolId: string; // Pool_A
  poolLabel: string; // A
  players: ScoreSheetPlayer[]; // group roster, in seed order
  matches: ScoreSheetMatch[]; // round-robin matches, in play order
  bestOf: number; // representative series length for the header
}

/** Everything needed to print one bracket match's fill-in sheet. */
export interface BracketScoreSheet {
  tournamentId: string;
  tournamentName: string;
  nodeId: string; // Node_R1_M1
  nodeLabel: string; // "Quarterfinal 1", "Final", ...
  playerA: ScoreSheetPlayer;
  playerB: ScoreSheetPlayer;
  bestOf: number; // series length for this match (odd)
  gamesToWin: number; // games needed to take the series (derived from bestOf)
}

/** Orders matches the way they are actually played: time slot, then table. */
function byPlayOrder(
  a: { timeSlot: number; tableIndex: number },
  b: { timeSlot: number; tableIndex: number },
): number {
  if (a.timeSlot !== b.timeSlot) return a.timeSlot - b.timeSlot;
  return a.tableIndex - b.tableIndex;
}

/**
 * 1-based position of `cell` within its group's play order (time slot, then
 * table) — the same "Match N" numbering used on the printed score sheet, so a
 * match reads identically wherever it is shown. Returns 0 when the cell's group
 * is unknown or the cell is not part of it.
 */
export function groupMatchOrder(tournament: TournamentState, cell: Cell): number {
  const group = tournament.groups[cell.poolId];
  if (!group) return 0;
  const ordered = group.cellIds
    .map((id): Cell | undefined => tournament.cells[id])
    .filter((c): c is Cell => c !== undefined)
    .sort(byPlayOrder);
  return ordered.findIndex((c) => c.id === cell.id) + 1;
}

/**
 * Seed for a player id, used to order the roster. Falls back to the numeric part
 * of the id (e.g. "P3" -> 3) when the Player record is missing, so a stray id
 * keeps its natural position instead of jumping to the end.
 */
function seedForId(seed: number | undefined, id: PlayerId): number {
  if (seed !== undefined) return seed;
  const digits = /(\d+)/.exec(id);
  return digits ? Number(digits[1]) : Number.MAX_SAFE_INTEGER;
}

/** Player entry for a sheet, falling back to the bare id for a missing record. */
function toScoreSheetPlayer(
  tournament: TournamentState,
  id: PlayerId,
): ScoreSheetPlayer {
  const player = tournament.players[id];
  return {
    id,
    seed: seedForId(player?.seed, id),
    name: player?.name ?? id,
  };
}

/**
 * Builds the score-sheet model for a single pool. Players are listed in seed
 * order; matches are listed in the order they are played (time slot, then
 * table), so a group can follow the sheet top-to-bottom.
 *
 * Throws if the pool id is unknown — callers should pass ids from
 * `tournament.groupOrder`.
 */
export function buildGroupScoreSheet(
  tournament: TournamentState,
  poolId: string,
): GroupScoreSheet {
  const group = tournament.groups[poolId];
  if (!group) {
    throw new Error(`Unknown pool: ${poolId}`);
  }

  const toPlayer = (id: PlayerId): ScoreSheetPlayer =>
    toScoreSheetPlayer(tournament, id);

  const players = group.playerIds
    .map(toPlayer)
    .sort((a, b) => a.seed - b.seed);

  // A cellId without a matching cell (a dangling reference) is dropped; the
  // lookup is typed as possibly-undefined so the filter narrows honestly.
  const matches = group.cellIds
    .map((cellId): Cell | undefined => tournament.cells[cellId])
    .filter((cell): cell is Cell => cell !== undefined)
    .sort(byPlayOrder)
    .map((cell, index): ScoreSheetMatch => {
      const bestOf = normalizeBestOf(cell.bestOf, DEFAULT_GROUP_BEST_OF);
      return {
        cellId: cell.id,
        order: index + 1,
        timeSlot: cell.timeSlot,
        timeSlotLabel: timeSlotLabel(cell.timeSlot),
        tableIndex: cell.tableIndex,
        playerA: toPlayer(cell.playerA),
        playerB: toPlayer(cell.playerB),
        bestOf,
        gamesToWin: gamesToWin(bestOf),
      };
    });

  const bestOf =
    matches[0]?.bestOf ??
    normalizeBestOf(tournament.config.groupBestOf, DEFAULT_GROUP_BEST_OF);

  return {
    tournamentId: tournament.id,
    tournamentName: tournament.name,
    poolId: group.id,
    poolLabel: group.label,
    players,
    matches,
    bestOf,
  };
}

/** Builds a score sheet for every pool, in display (`groupOrder`) order. */
export function buildScoreSheets(tournament: TournamentState): GroupScoreSheet[] {
  return tournament.groupOrder.map((poolId) =>
    buildGroupScoreSheet(tournament, poolId),
  );
}

/** A bracket node whose participants are both resolved to real players. */
export type PrintableBracketNode = BracketNode & {
  playerA: PlayerId;
  playerB: PlayerId;
};

/**
 * True once a bracket match can be collected on paper: both participants are
 * resolved, so the sheet can name its players. A form headed "Winner of
 * Semifinal 1" cannot collect a result. `buildBracketScoreSheet` enforces the
 * same rule, and the UI shows a node's print button only when this holds.
 */
export function isBracketMatchPrintable(
  node: BracketNode,
): node is PrintableBracketNode {
  return node.playerA !== null && node.playerB !== null;
}

/**
 * Builds the score-sheet model for a single bracket match. Throws if the node
 * id is unknown or the node is not printable yet; callers should pass ids from
 * `tournament.nodeOrder` and gate on `isBracketMatchPrintable`.
 */
export function buildBracketScoreSheet(
  tournament: TournamentState,
  nodeId: string,
): BracketScoreSheet {
  const node = tournament.nodes[nodeId];
  if (!node) {
    throw new Error(`Unknown bracket node: ${nodeId}`);
  }
  if (!isBracketMatchPrintable(node)) {
    throw new Error(
      `Bracket node ${nodeId} has unresolved players; nothing to print yet.`,
    );
  }

  const bestOf = normalizeBestOf(node.bestOf, DEFAULT_BRACKET_BEST_OF);
  return {
    tournamentId: tournament.id,
    tournamentName: tournament.name,
    nodeId: node.id,
    nodeLabel: node.label,
    playerA: toScoreSheetPlayer(tournament, node.playerA),
    playerB: toScoreSheetPlayer(tournament, node.playerB),
    bestOf,
    gamesToWin: gamesToWin(bestOf),
  };
}
